import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, CheckCircle2, CreditCard, Loader2, Lock, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { formatPlanArs } from "@/lib/pricing-catalog";
import type { SaasBankTransfer } from "@/lib/saas-billing-config";
import {
  SAAS_CHECKOUT_METHOD_LABELS,
  type SaasCheckoutPaymentMethod,
  type SaasRoutableProvider,
} from "@/lib/saas-payment-routing";

/** Providers que /contratar puede iniciar (backend). */
export type PayProvider = "uala" | "manual_transfer" | "payway" | "mercadopago" | "dlocal";

export function computePeriodTotal(priceMonthly: number, billingMode: string, annualDiscountPct: number): number {
  if (billingMode === "annual_oneshot") return Math.round(priceMonthly * 12 * (1 - annualDiscountPct / 100));
  return priceMonthly;
}

interface PaywayCfg {
  configured?: boolean;
  publicKey?: string;
  apiBaseUrl?: string;
}

/**
 * Identificación + confianza del procesador cuando Structura captura la
 * tarjeta directamente (SCRUM-257). No hardcodeado a Payway en la forma —
 * parametrizado por processor, para poder sumar otro proveedor de captura
 * directa sin duplicar el componente. Sin logo: no hay ningún asset oficial
 * de Payway en el repo — identificación tipográfica en vez de fabricar uno.
 */
interface CardProcessorTrust {
  name: string;
  legalName: string;
  legalAddress: string;
  legalCuit: string;
}

const PAYWAY_TRUST: CardProcessorTrust = {
  name: "Payway",
  legalName: "Payway S.A.U.",
  legalAddress: "Lavardén 247 (C1437FBE), CABA, Argentina",
  legalCuit: "CUIT: 30-71766440-6",
};

function CardProcessorTrustBadge({ processor }: { processor: CardProcessorTrust }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-3">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <div className="text-[11px] leading-relaxed text-muted-foreground">
        <p className="text-xs font-semibold text-foreground">Pago procesado de forma segura por {processor.name}</p>
        <p className="mt-1">{processor.legalName} — {processor.legalAddress} — {processor.legalCuit}</p>
        <p>Todos los derechos reservados.</p>
      </div>
    </div>
  );
}

type MethodOption = {
  method: SaasCheckoutPaymentMethod;
  label: string;
  provider: SaasRoutableProvider;
};

interface Props {
  priceMonthly: number;
  billingMode: string;
  annualDiscountPct?: number;
  planName: string;
  businessName: string;
  ownerDni?: string;
  bankTransferArs?: SaasBankTransfer | null;
  onPay: (provider: PayProvider, extra?: { token?: string; bin?: string }) => Promise<void>;
  onBack: () => void;
  onCancel: () => void;
  error?: string | null;
  loading?: boolean;
}

export function ContratarPaymentStep({
  priceMonthly, billingMode, annualDiscountPct = 25,
  planName, businessName, ownerDni = "", bankTransferArs, onPay, onBack, onCancel, error, loading,
}: Props) {
  const arsTotal = computePeriodTotal(priceMonthly, billingMode, annualDiscountPct);
  const period = billingMode === "annual_oneshot" ? "/año" : "/mes";

  const [options, setOptions] = useState<MethodOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [method, setMethod] = useState<SaasCheckoutPaymentMethod | null>(null);
  const [paywayCfg, setPaywayCfg] = useState<PaywayCfg | null>(null);

  const [tokenizing, setTokenizing] = useState(false);
  const [tokenizeError, setTokenizeError] = useState<string | null>(null);

  const [dni, setDni] = useState(ownerDni);
  const [cardNum, setCardNum] = useState("");
  const [cardName, setCardName] = useState("");
  const [expM, setExpM] = useState("");
  const [expY, setExpY] = useState("");
  const [cvv, setCvv] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOptionsLoading(true);
      try {
        const [methodsRes, cfgRes] = await Promise.all([
          fetch(`/api/self-service/payment-methods?billingMode=${encodeURIComponent(billingMode)}`),
          fetch("/api/self-service/payway-config"),
        ]);
        const methodsJson = (await methodsRes.json()) as {
          methods?: Array<{ method: SaasCheckoutPaymentMethod; label: string; provider: SaasRoutableProvider | null; available?: boolean }>;
        };
        const cfg = (await cfgRes.json()) as PaywayCfg;
        if (cancelled) return;
        setPaywayCfg(cfg);
        const opts = (methodsJson.methods ?? [])
          .filter((m): m is MethodOption => Boolean(m.available && m.provider))
          .map((m) => ({ method: m.method, label: m.label || SAAS_CHECKOUT_METHOD_LABELS[m.method], provider: m.provider! }));
        setOptions(opts);
        setMethod((prev) => {
          if (prev && opts.some((o) => o.method === prev)) return prev;
          return opts[0]?.method ?? null;
        });
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [billingMode]);

  const selected = useMemo(
    () => options.find((o) => o.method === method) ?? null,
    [options, method],
  );
  const provider = selected?.provider ?? null;
  const needsCardForm = provider === "payway";
  const paywayAvailable = Boolean(paywayCfg?.configured && paywayCfg.publicKey && paywayCfg.apiBaseUrl);

  const tokenizePayway = useCallback(async () => {
    if (!paywayCfg?.apiBaseUrl || !paywayCfg.publicKey) return;
    const tokRes = await fetch(`${paywayCfg.apiBaseUrl}/tokens`, {
      method: "POST",
      headers: { apikey: paywayCfg.publicKey, "Content-Type": "application/json" },
      body: JSON.stringify({
        card_number: cardNum.replace(/\s/g, ""),
        card_expiration_month: expM,
        card_expiration_year: expY.length === 2 ? `20${expY}` : expY,
        security_code: cvv,
        card_holder_name: cardName,
        card_holder_identification: { type: "dni", number: dni },
      }),
    });
    const tok = (await tokRes.json()) as { id?: string; bin?: string; error?: string };
    if (!tok.id) throw new Error(tok.error ?? "No se pudo tokenizar la tarjeta");
    await onPay("payway", { token: tok.id, bin: tok.bin });
  }, [paywayCfg, cardNum, expM, expY, cvv, cardName, dni, onPay]);

  // CTA sin nombre de pasarela (SCRUM-225).
  const ctaLabel = `Pagar — ${formatPlanArs(arsTotal)}`;

  const submit = () => {
    if (!provider) return;
    if (provider === "payway") {
      if (!paywayAvailable) return;
      setTokenizeError(null);
      setTokenizing(true);
      tokenizePayway()
        .catch((e) => setTokenizeError(e instanceof Error ? e.message : "No se pudo tokenizar la tarjeta"))
        .finally(() => setTokenizing(false));
      return;
    }
    if (provider === "uala") {
      void onPay("uala");
      return;
    }
    if (provider === "mercadopago") {
      void onPay("mercadopago");
      return;
    }
    if (provider === "dlocal") {
      void onPay("dlocal");
      return;
    }
    void onPay("manual_transfer");
  };

  const methodIcon = (m: SaasCheckoutPaymentMethod) => {
    if (m === "bank_transfer") return Building2;
    if (m === "debit_card") return Wallet;
    return CreditCard;
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="rounded-xl border border-border bg-muted/30 p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Plan</span>
            <span className="text-base text-foreground">{planName}</span>
          </div>
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Negocio</span>
            <span className="text-base text-foreground">{businessName}</span>
          </div>
          <div className="mt-4 flex flex-col gap-1 border-t border-border/50 pt-4">
            <div className="flex items-baseline justify-between">
              <span className="text-base text-foreground">Total</span>
              <span className="text-lg font-bold tabular-nums text-foreground whitespace-nowrap">{formatPlanArs(arsTotal)}{period}</span>
            </div>
            <p className="text-xs text-muted-foreground">Cobro en pesos argentinos (ARS)</p>
          </div>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-foreground">Método de pago</h3>
      <p className="text-xs text-muted-foreground">Elegí el método. El proveedor de cobro lo resuelve Structura automáticamente.</p>

      {optionsLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando métodos…</p>
      ) : options.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay métodos de pago disponibles. Revisá Routing y pasarelas conectadas en Admin → Cobros.</p>
      ) : (
        <div className="space-y-3">
          {options.map((opt) => {
            const Icon = methodIcon(opt.method);
            const active = method === opt.method;
            return (
              <button
                key={opt.method}
                type="button"
                onClick={() => setMethod(opt.method)}
                className={cn(
                  "flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors",
                  active ? "border-primary bg-primary/5" : "border-border bg-muted/30",
                )}
              >
                <Icon className="h-5 w-5 text-primary" />
                <span className="text-base text-foreground">{opt.label}</span>
                {active && <CheckCircle2 className="ml-auto h-5 w-5 text-primary" />}
              </button>
            );
          })}
        </div>
      )}

      {provider === "manual_transfer" && bankTransferArs && (
        <div className="space-y-1.5 rounded-xl border border-border bg-muted p-4 text-sm text-muted-foreground">
          <p><strong className="text-foreground">Titular:</strong> {bankTransferArs.holder}</p>
          <p><strong className="text-foreground">CBU:</strong> {bankTransferArs.cbu}</p>
          <p><strong className="text-foreground">Alias:</strong> {bankTransferArs.alias}</p>
          <p><strong className="text-foreground">Banco:</strong> {bankTransferArs.bank}</p>
          {bankTransferArs.notes && <p className="mt-2 italic">{bankTransferArs.notes}</p>}
        </div>
      )}

      {needsCardForm && (
        paywayAvailable ? (
          <div className="space-y-3 rounded-xl border border-border p-4">
            <CardProcessorTrustBadge processor={PAYWAY_TRUST} />
            <div className="space-y-1">
              <Label className="text-xs">DNI del titular</Label>
              <Input value={dni} onChange={(e) => setDni(e.target.value)} inputMode="numeric" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Número de tarjeta</Label>
              <Input value={cardNum} onChange={(e) => setCardNum(e.target.value)} inputMode="numeric" autoComplete="cc-number" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Titular</Label>
              <Input value={cardName} onChange={(e) => setCardName(e.target.value)} autoComplete="cc-name" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Mes</Label>
                <Input value={expM} onChange={(e) => setExpM(e.target.value)} placeholder="MM" maxLength={2} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Año</Label>
                <Input value={expY} onChange={(e) => setExpY(e.target.value)} placeholder="AA" maxLength={4} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">CVV</Label>
                <Input value={cvv} onChange={(e) => setCvv(e.target.value)} placeholder="CVV" maxLength={4} />
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            El Routing eligió tarjeta tokenizada, pero Payway no está configurado. Usá otro método o conectá Payway en Admin → Cobros.
          </p>
        )
      )}

      {error || tokenizeError ? <p className="text-sm text-destructive">{error || tokenizeError}</p> : null}

      <div className="flex flex-col gap-2">
        <Button
          size="lg"
          className="w-full"
          disabled={loading || tokenizing || !provider || optionsLoading || (needsCardForm && !paywayAvailable)}
          onClick={submit}
        >
          {loading || tokenizing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {ctaLabel}
        </Button>
        <Button type="button" variant="ghost" onClick={onBack} disabled={loading}>Volver</Button>
        <Button type="button" variant="ghost" className="text-muted-foreground" onClick={onCancel} disabled={loading}>Cancelar</Button>
        <p className="text-center text-[11px] text-muted-foreground">Al proceder, aceptás nuestros Términos de Servicio y Privacidad.</p>
      </div>
    </div>
  );
}
